import type { DynamicClient } from '@dynamic-labs-sdk/client';
import {
  createApiClient,
  getBuffer,
  getCore,
} from '@dynamic-labs-sdk/client/core';
import type { Transaction } from '@solana/web3.js';
import { VersionedTransaction } from '@solana/web3.js';

import { SponsorTransactionError } from '../../../errors/SponsorTransactionError/SponsorTransactionError';
import { isVersionedTransaction } from '../../../utils/isVersionedTransaction';

type SponsorTransactionParams = {
  transaction: Transaction | VersionedTransaction;
};

/**
 * Sponsors a Solana transaction using the Dynamic backend.
 *
 * Sends the transaction to Dynamic's sponsorship API, which replaces the
 * fee payer with a sponsored account. This allows users to execute
 * transactions without holding SOL for gas fees.
 *
 * @param params - The parameters object containing the transaction
 * @param params.transaction - The Solana transaction to sponsor
 * @param client - The Dynamic client instance used for API calls and state access
 * @returns The sponsored transaction as a VersionedTransaction
 * @throws {SponsorTransactionError} If the API response is missing the transaction field
 */
export const sponsorTransaction = async (
  { transaction }: SponsorTransactionParams,
  client: DynamicClient
): Promise<VersionedTransaction> => {
  const core = getCore(client);

  const serialized = isVersionedTransaction(transaction)
    ? transaction.serialize()
    : transaction.serialize({
        requireAllSignatures: false,
        verifySignatures: false,
      });

  const base64Transaction = getBuffer()
    .from(serialized as Uint8Array)
    .toString('base64');

  const apiClient = createApiClient({}, client);

  let data;

  try {
    data = await apiClient.sponsorSVMTransaction({
      environmentId: core.environmentId,
      sponsorSVMTransactionRequest: {
        transaction: base64Transaction,
      },
    });
  } catch (error) {
    const cause = error instanceof Error ? error : null;
    throw new SponsorTransactionError(cause);
  }

  if (!data.transaction) {
    throw new SponsorTransactionError();
  }

  const buffer = getBuffer().from(data.transaction, 'base64');

  return VersionedTransaction.deserialize(new Uint8Array(buffer));
};
